import { useEffect, useRef } from 'react';

const STORAGE_PREFIX = 'scroll-restore:';
const DEFAULT_TTL_MS = 5 * 60 * 1000;
const MAX_RESTORE_ATTEMPTS = 20;
const SCROLL_TOLERANCE_PX = 2;

type StoredScroll = {
    scrollY: number;
    savedAt: number;
};

/**
 * Scrolls the window to `targetY`, retrying on subsequent animation frames while
 * the target is still out of reach. Meant for pages whose content is rendered
 * progressively (async data, images loading): a single `scrollTo` fires before
 * the document is tall enough and gets clamped, this keeps re-applying it until
 * the offset is reached or the document cannot grow any further.
 */
export function restoreScrollPosition(targetY: number, maxAttempts = MAX_RESTORE_ATTEMPTS) {
    if (targetY <= 0 || typeof window === 'undefined') {
        return;
    }
    let attempts = 0;
    const step = () => {
        const maxY = Math.max(document.body.scrollHeight - window.innerHeight, 0);
        window.scrollTo({ top: Math.min(targetY, maxY), behavior: 'auto' });
        attempts += 1;
        if (Math.abs(window.scrollY - targetY) > SCROLL_TOLERANCE_PX && attempts < maxAttempts) {
            requestAnimationFrame(step);
        }
    };
    requestAnimationFrame(step);
}

type UseScrollRestorationOptions = {
    /** Unique key identifying the scrollable page (e.g. `webapp-home`). */
    key: string;
    /**
     * Becomes `true` once the page content is fully rendered (all async data
     * loaded). The scroll position is only restored after this flips to `true`,
     * so the document is tall enough for the target offset to be reachable.
     */
    ready: boolean;
    /** Disables tracking and restoration entirely when `false`. */
    enabled?: boolean;
    /** Maximum age of a stored position before it is ignored. */
    ttlMs?: number;
};

/**
 * Persists the window scroll position when the component unmounts and restores
 * it once `ready` is `true`. Meant for fully client-rendered pages whose content
 * loads progressively, where the browser's native scroll restoration fires too
 * early (while the document is still short) and clamps the offset to the top.
 */
export function useScrollRestoration({
    key,
    ready,
    enabled = true,
    ttlMs = DEFAULT_TTL_MS,
}: UseScrollRestorationOptions) {
    const storageKey = `${STORAGE_PREFIX}${key}`;
    const scrollYRef = useRef(0);
    const hasRestoredRef = useRef(false);

    // Keep track of the latest scroll position.
    useEffect(() => {
        if (!enabled) {
            return;
        }
        const onScroll = () => {
            scrollYRef.current = window.scrollY;
        };
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, [enabled]);

    // Persist the position when navigating away (component unmount).
    useEffect(() => {
        if (!enabled) {
            return;
        }
        return () => {
            try {
                const payload: StoredScroll = { scrollY: scrollYRef.current, savedAt: Date.now() };
                sessionStorage.setItem(storageKey, JSON.stringify(payload));
            } catch {
                // Ignore storage errors (private mode, quota, ...).
            }
        };
    }, [enabled, storageKey]);

    // Restore the position once the page content is ready.
    useEffect(() => {
        if (!enabled || !ready || hasRestoredRef.current) {
            return;
        }
        hasRestoredRef.current = true;

        let raw: string | null = null;
        try {
            raw = sessionStorage.getItem(storageKey);
            // Consume the entry so the position is only restored once per navigation.
            sessionStorage.removeItem(storageKey);
        } catch {
            return;
        }
        if (!raw) {
            return;
        }

        let stored: StoredScroll | null = null;
        try {
            stored = JSON.parse(raw) as StoredScroll;
        } catch {
            return;
        }
        if (!stored?.scrollY || !stored.savedAt || Date.now() - stored.savedAt > ttlMs) {
            return;
        }

        restoreScrollPosition(stored.scrollY);
    }, [enabled, ready, storageKey, ttlMs]);
}
